import React from "react";
import {  Box,
  Paper,
  TextField,
  IconButton,
  Typography,
  Button,
  alpha,
  useTheme,} from "@mui/material";
import Grid from "@mui/material/Grid";
import { Save, Edit, Print, InfoOutlined } from "@mui/icons-material";
import { renderPumpLogV2 } from "../../print-v2";
import { useSettings } from "../../Contexts/SettingsContext";
import { useTranslation } from "react-i18next";
import { getVoyageNumber } from "../../utils/domainSelectors";

const VoyageCargoInfoTable = ({
  currentVoyage,
  trip,
  cargo,
  selectedCargoIndex,
  selectedTripId,
  isLoadLog,
  companyLogo,
  manifolds,
  connectionSize,
  setManifolds,
  setConnectionSize,
  isLocked,
  handleLock,
  readOnly,
}) => {
  const { t } = useTranslation("logging");
  const { is24Hour } = useSettings();
  const theme = useTheme();
  // --- DATA MAPPING (Fixar N/A-problemet) ---
  // V2 använder voyageNumber i voyage-objektet
  const displayVoyNo = getVoyageNumber(currentVoyage) || "N/A";

  // Hanterar om cargo skickas som ett objekt {cargo: 'Oil'} eller sträng 'Oil'
  const displayCargoName =
    cargo?.cargo || (typeof cargo === "string" ? cargo : "N/A");
  const displayQty = cargo?.quantity || cargo?.amount || "0";
  const displayUnit = cargo?.unit || "";

  // Helper för att rendera infofält snyggt
  const InfoField = ({ label, value, secondary }) => (
    <Box sx={{ mb: 1.5 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        fontWeight="700"
        sx={{
          textTransform: "uppercase",
          letterSpacing: 0.5,
          display: "block",
        }}
      >
        {label}
      </Typography>
      <Typography variant="body1" fontWeight="600" sx={{ minHeight: "1.5rem" }}>
        {value || "—"} {secondary || ""}
      </Typography>
    </Box>
  );

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 4,
        border: "1px solid",
        borderColor: "divider",
        mb: 3,
        background: `linear-gradient(to bottom right, #ffffff, ${alpha(
          theme.palette.background.default,
          0.5
        )})`,
      }}
    >
      {/* Header med Titel och Print */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              p: 1,
              borderRadius: 1.5,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: "primary.main",
              display: "flex",
            }}
          >
            <InfoOutlined />
          </Box>
          <Typography variant="h6" fontWeight="800">
            {isLoadLog
              ? t("voyageInfo.titleLoading")
              : t("voyageInfo.titleDischarging")}
          </Typography>
        </Box>

        <Button
          variant="outlined"
          startIcon={<Print />}
          onClick={() =>
            renderPumpLogV2({
              currentVoyage,
              selectedTripId,
              selectedCargoIndex,
              isLoadLog,
              companyLogo,
              is24Hour,
            })
          }
          sx={{ borderRadius: 2, fontWeight: 700, px: 3 }}
        >
          {t("print")}
        </Button>
      </Box>

      <Grid container spacing={4}>
        {/* Vänster kolumn: Voyage & Trip Info */}
        <Grid size={{ xs: 12, md: 4 }}>
          <InfoField label={t("voyageInfo.voyage")} value={displayVoyNo} />
          <InfoField label={t("voyageInfo.port")} value={trip?.to} />
          <InfoField label={t("voyageInfo.jetty")} value={trip?.jetty} />
        </Grid>

        {/* Mitten kolumn: Cargo Info */}
        <Grid size={{ xs: 12, md: 4 }}>
          <InfoField label={t("voyageInfo.cargo")} value={displayCargoName} />
          <InfoField
            label={t("voyageInfo.totalQuantity")}
            value={displayQty}
            secondary={displayUnit}
          />
          <InfoField label={t("voyageInfo.date")} value={trip?.date} />
        </Grid>

        {/* Höger kolumn: Inställningar (Manifolds etc) */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Box
            sx={{
              p: 2,
              borderRadius: 3,
              bgcolor: isLocked
                ? alpha(theme.palette.action.disabledBackground, 0.2)
                : alpha(theme.palette.primary.main, 0.03),
              border: "1px dashed",
              borderColor: isLocked ? "divider" : "primary.light",
              transition: "all 0.2s ease",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="subtitle2" fontWeight="800">
                {t("voyageInfo.operationalSettings")}
              </Typography>
              <IconButton
                size="small"
                onClick={handleLock}
                disabled={readOnly}
                color={isLocked ? "default" : "primary"}
                sx={{
                  bgcolor: "background.paper",
                  boxShadow: 1,
                  "&:hover": {
                    bgcolor: "background.paper",
                    transform: "scale(1.1)",
                  },
                }}
              >
                {isLocked ? (
                  <Edit fontSize="small" />
                ) : (
                  <Save fontSize="small" />
                )}
              </IconButton>
            </Box>

            <Grid container spacing={2}>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label={t("voyageInfo.manifolds")}
                  variant="outlined"
                  size="small"
                  value={manifolds || ""}
                  onChange={(e) => setManifolds(e.target.value)}
                  disabled={isLocked || readOnly}
                  sx={{ bgcolor: "background.paper", borderRadius: 1 }}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label={t("voyageInfo.connectionSize")}
                  variant="outlined"
                  size="small"
                  value={connectionSize || ""}
                  onChange={(e) => setConnectionSize(e.target.value)}
                  disabled={isLocked || readOnly}
                  sx={{ bgcolor: "background.paper", borderRadius: 1 }}
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default VoyageCargoInfoTable;
